import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Sparkles, Star } from "lucide-react";
import Seo from "../components/Seo";
import Reviews from "../components/Reviews";
import CtaBanner from "../components/CtaBanner";
import Gallery from "../components/Gallery";
import GiftVouchers from "../components/GiftVouchers";
import Faq from "../components/Faq";
import { BUSINESS, IMAGES, BEAUTIX_IG } from "../data/business";
import { REVIEW_STATS } from "../data/reviews";

const PILLARS = [
  {
    key: "nails",
    title: "Stylizacja Paznokci",
    kicker: "01",
    tagline: "Hybryda · Żel · Tytan",
    image: IMAGES.nails,
    href: "/stylizacja-paznokci-katowice",
    alt: "Stylizacja paznokci Katowice — realizacja BEAUTIX",
  },
  {
    key: "podology",
    title: "Podologia",
    kicker: "02",
    tagline: "Wrastające paznokcie · Klamry · Pedicure leczniczy",
    image: IMAGES.podiatry,
    href: "/podologia-katowice",
    alt: "Gabinet podologiczny Katowice — leczenie stóp BEAUTIX",
  },
  {
    key: "face",
    title: "Kosmetyka Twarzy",
    kicker: "03",
    tagline: "Oczyszczanie · Mezoterapia · Kobido",
    image: IMAGES.face,
    href: "/kosmetyka-twarzy-katowice",
    alt: "Kosmetyka twarzy Katowice — zabiegi BEAUTIX",
  },
];

const USP = [
  { n: "25", label: "lat na rynku beauty" },
  { n: "4,9", label: "średnia ocena z 117 opinii" },
  { n: "3", label: "specjalizacje pod jednym dachem" },
  { n: "5×", label: "laureat Złota Firma 2022 – 2026" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="BEAUTIX Katowice — Stylizacja Paznokci, Podologia, Kosmetyka Twarzy"
        description="Studio Kosmetyki Nowoczesnej BEAUTIX Katowice ul. Ligocka 80. Manicure hybrydowy, żelowy, tytanowy, profesjonalna podologia, kosmetyka twarzy. 4,9/5 (117 opinii). Zadzwoń 794 000 270."
        keywords="stylizacja paznokci Katowice, manicure hybrydowy Katowice, podolog Katowice, kosmetyka twarzy Katowice, salon kosmetyczny Katowice Ligota, BEAUTIX"
        path="/"
      />

      {/* HERO — mobile: full-bleed image background · desktop: split 50/50 */}
      <section
        className="relative overflow-hidden -mt-24 md:-mt-28 bg-[var(--beautix-cream)]"
        data-testid="hero-section"
      >
        {/* MOBILE / TABLET: image as background */}
        <div className="lg:hidden relative min-h-[100svh]">
          <img
            src={IMAGES.heroHome}
            alt="Elegancki manicure BEAUTIX Katowice — nude z kropkami i biżuteria"
            className="absolute inset-0 w-full h-full object-cover object-[70%_center]"
            loading="eager"
            fetchPriority="high"
          />
          {/* Cream gradient veil bottom→top for text legibility */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(252,247,248,0.85) 0%, rgba(252,247,248,0.55) 45%, rgba(252,247,248,0.2) 75%, rgba(252,247,248,0) 100%)",
            }}
          />
          <div className="relative px-6 sm:px-8 pt-32 pb-16 fade-up">
            <span className="eyebrow flex items-center gap-3">
              <Sparkles className="w-3.5 h-3.5 text-[var(--beautix-gold)]" strokeWidth={1.5} />
              Studio Kosmetyki Nowoczesnej · Katowice
            </span>
            <h2 className="font-serif-display mt-5 text-[2.5rem] sm:text-5xl leading-[0.98] tracking-tight text-[var(--beautix-ink)]">
              Piękno ukryte
              <br />
              w <span className="italic text-[var(--beautix-pink)]">detalu</span>.
            </h2>
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-[var(--beautix-ink)]/85 font-light">
              Odwiedź Studio Kosmetyki Nowoczesnej BEAUTIX w Katowicach przy
              ul. Ligockiej 80. Nowoczesne zabiegi na twarz, modny manicure
              hybrydowy oraz profesjonalna podologia — pod jednym dachem.
            </p>
            <div className="mt-7 flex flex-col gap-3">
              <a
                href={`tel:${BUSINESS.phones[1].tel}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-3 bg-[var(--beautix-pink)] hover:bg-[var(--beautix-pink-dark)] text-white px-6 py-4 rounded-full text-sm font-medium tracking-wide transition-colors"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                Zadzwoń {BUSINESS.phones[1].number}
              </a>
              <Link
                to="/cennik"
                data-testid="hero-pricing-btn"
                className="inline-flex items-center justify-center gap-3 border border-[var(--beautix-ink)]/70 bg-white/60 backdrop-blur-sm px-6 py-4 rounded-full text-sm font-medium tracking-wide"
              >
                Zobacz cennik
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm text-[var(--beautix-ink)]/80">
              <div className="flex gap-1 text-[var(--beautix-gold)]">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />
                ))}
              </div>
              <span>
                <strong className="text-[var(--beautix-ink)]">{REVIEW_STATS.average}/5</strong> ·{" "}
                {REVIEW_STATS.total} opinii
              </span>
            </div>
          </div>
          {/* Mobile 25 badge */}
          <div className="absolute bottom-6 left-6 flex items-center gap-3 bg-white/90 backdrop-blur-md px-4 py-2.5 rounded-full border border-white/70">
            <div className="w-8 h-8 rounded-full bg-[var(--beautix-pink)] text-white flex items-center justify-center text-xs font-medium">
              25
            </div>
            <div className="text-xs">
              <p className="font-medium text-[var(--beautix-ink)]">25 lat doświadczenia</p>
              <p className="text-[var(--beautix-muted)] text-[10px]">Ligocka 80, Katowice</p>
            </div>
          </div>
        </div>

        {/* DESKTOP: split 50/50 */}
        <div className="hidden lg:grid grid-cols-2 lg:h-[min(100svh,900px)]">
          {/* Left — text with blurred continuation background */}
          <div className="relative flex flex-col justify-center px-6 md:px-12 lg:px-16 xl:px-20 pt-28 md:pt-32 pb-20 lg:pb-32 overflow-hidden">
            <img
              src="/assets/ig/hero-bg-blur.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, rgba(252,247,248,0.92) 0%, rgba(252,247,248,0.78) 55%, rgba(252,247,248,0.55) 100%)",
              }}
            />
            <div className="absolute inset-0 grain pointer-events-none" />

            <div className="fade-up max-w-xl relative">
              <span className="eyebrow flex items-center gap-3">
                <Sparkles className="w-3.5 h-3.5 text-[var(--beautix-gold)]" strokeWidth={1.5} />
                Studio Kosmetyki Nowoczesnej · Katowice
              </span>
              <h1 className="font-serif-display mt-5 text-5xl lg:text-[4.5rem] xl:text-[5rem] leading-[0.98] tracking-tight text-[var(--beautix-ink)]">
                Piękno ukryte
                <br />
                w <span className="italic text-[var(--beautix-pink)]">detalu</span>.
              </h1>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-[var(--beautix-muted)] font-light">
                Chcesz podarować sobie chwilę relaksu i zadbać o swój wygląd?
                Odwiedź Studio Kosmetyki Nowoczesnej BEAUTIX w Katowicach przy
                ul. Ligockiej 80! Odkryj nowoczesne zabiegi na twarz, w tym
                oczyszczanie wodorowe, które przywróci Twojej cerze blask.
                Podkreśl swój styl, wybierając modne paznokcie i trwały
                manicure hybrydowy. Dbamy też o Twoje zdrowie – nasza specjalność
                to profesjonalna podologia. Wybierz Twój ulubiony salon
                kosmetyczny Katowice Ligota i poczuj się pięknie.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 fade-up fade-up-3">
                <a
                  href={`tel:${BUSINESS.phones[1].tel}`}
                  className="inline-flex items-center gap-3 bg-[var(--beautix-pink)] hover:bg-[var(--beautix-pink-dark)] text-white px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-colors"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  Zadzwoń {BUSINESS.phones[1].number}
                </a>
                <Link
                  to="/cennik"
                  className="inline-flex items-center gap-3 border border-[var(--beautix-ink)]/70 hover:bg-white px-8 py-4 rounded-full text-sm font-medium tracking-wide transition-colors"
                >
                  Zobacz cennik
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-4 text-sm text-[var(--beautix-muted)] fade-up fade-up-4">
                <div className="flex gap-1 text-[var(--beautix-gold)]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />
                  ))}
                </div>
                <span>
                  <strong className="text-[var(--beautix-ink)]">{REVIEW_STATS.average}/5</strong> ·{" "}
                  {REVIEW_STATS.total} opinii Google, Facebook, panoramafirm.pl
                </span>
              </div>

              <div className="mt-12 flex items-center gap-4 pt-8 border-t border-[var(--beautix-line)]">
                <div className="w-10 h-10 rounded-full bg-[var(--beautix-pink)] text-white flex items-center justify-center text-xs font-medium">
                  25
                </div>
                <div className="text-sm">
                  <p className="font-medium text-[var(--beautix-ink)]">25 lat doświadczenia</p>
                  <p className="text-[var(--beautix-muted)] text-xs">Zespół specjalistek · Ligocka 80</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — image shown in full, blurred continuation as backdrop */}
          <div className="relative flex items-center justify-center overflow-hidden">
            <img
              src="/assets/ig/hero-bg-blur.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-x-[-1]"
            />
            <img
              src={IMAGES.heroHome}
              alt="Elegancki manicure BEAUTIX Katowice — nude z kropkami i biżuteria"
              className="relative w-full h-full object-contain object-left"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* Marquee eyebrow keywords */}
      <div className="bg-[var(--beautix-cream)] border-y border-[var(--beautix-line)] py-4 overflow-hidden">
        <div className="whitespace-nowrap flex gap-10 justify-around text-xs tracking-[0.3em] uppercase text-[var(--beautix-muted)]">
          <span>Manicure hybrydowy</span>
          <span className="text-[var(--beautix-gold)]">✦</span>
          <span>Paznokcie żelowe</span>
          <span className="text-[var(--beautix-gold)]">✦</span>
          <span>Podologia</span>
          <span className="text-[var(--beautix-gold)]">✦</span>
          <span>Kosmetyka twarzy</span>
          <span className="text-[var(--beautix-gold)]">✦</span>
          <span>Katowice Ligota</span>
        </div>
      </div>

      {/* USP numbers */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid grid-cols-2 lg:grid-cols-4 gap-10 border-b border-[var(--beautix-line)]"
        data-testid="usp-section"
      >
        {USP.map((u) => (
          <div key={u.label}>
            <p className="font-serif-display text-5xl md:text-6xl text-[var(--beautix-pink)]">
              {u.n}
            </p>
            <p className="text-sm text-[var(--beautix-muted)] mt-3 max-w-[200px]">
              {u.label}
            </p>
          </div>
        ))}
      </section>

      {/* Three service pillars */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="services-overview">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-4">Trzy specjalizacje, jeden gabinet</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Pełen zakres usług{" "}
              <span className="italic text-[var(--beautix-pink)]">beauty</span>{" "}
              w sercu Katowic.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-base text-[var(--beautix-muted)] leading-relaxed">
              Od 25 lat łączymy pasję, wiedzę i najnowsze techniki w stylizacji paznokci, podologii
              i kosmetykę twarzy. Wybierz to, czego dziś potrzebujesz.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <Link
              key={p.key}
              to={p.href}
              data-testid={`pillar-card-${p.key}`}
              className="group flex flex-col border border-[var(--beautix-line)] bg-white hover:border-[var(--beautix-pink)] transition-colors"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                  loading={i === 0 ? "eager" : "lazy"}
                />
                <span className="absolute top-4 left-4 text-xs tracking-[0.3em] uppercase text-white/90 bg-black/25 backdrop-blur-md px-3 py-1.5 rounded-full">
                  {p.kicker}
                </span>
              </div>
              <div className="p-8 flex flex-col gap-3">
                <h3 className="font-serif-display text-3xl">{p.title}</h3>
                <p className="text-sm text-[var(--beautix-muted)]">{p.tagline}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--beautix-pink)]">
                  Zobacz szczegóły
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Real BEAUTIX gallery — marquee */}
      <Gallery variant="marquee" />

      {/* About snippet — overlapping */}
      <section className="bg-[var(--beautix-cream)] py-24 md:py-32" data-testid="about-snippet">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden rounded-tr-[80px]">
              <img
                src={BEAUTIX_IG.dotsFrench.src}
                alt="DOTS nails — french z kropkami · BEAUTIX Katowice"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <figure className="hidden md:block absolute -right-6 -bottom-8 w-52 aspect-square overflow-hidden border-8 border-[var(--beautix-cream)]">
              <img
                src={BEAUTIX_IG.wiosennePaznokcie.src}
                alt="Wiosenne paznokcie — kropki na nude · BEAUTIX"
                className="w-full h-full object-cover"
              />
            </figure>
          </div>
          <div className="lg:col-span-7 lg:pl-12">
            <p className="eyebrow mb-4">O Studiu BEAUTIX</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
              Nowoczesne piękno oparte na czystości,
              <br />
              <span className="italic text-[var(--beautix-pink)]">pasji</span> i wiedzy.
            </h2>
            <p className="text-lg text-[var(--beautix-muted)] leading-relaxed mb-6 font-light">
              Salon powstał z miłości do detalu. Nasz zespół — trzy doświadczone
              stylistki, kosmetolog i podolog — łączy pasję z ciągłą edukacją.
              Sięgamy po najlepsze marki: NeoNail Premium, Semilac Diamond,
              markowe kosmetyki gabinetowe oraz sprzęt medyczny w podologii.
            </p>
            <p className="text-lg text-[var(--beautix-muted)] leading-relaxed mb-10 font-light">
              Wierzymy, że wizyta u kosmetyczki to czas dla Ciebie. Dlatego
              w BEAUTIX zadbamy o miłą atmosferę, precyzję każdego zabiegu
              i pełną higienę.
            </p>
            <Link
              to="/o-nas"
              data-testid="about-more-btn"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--beautix-pink)] link-underline"
            >
              Poznaj naszą historię
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <Reviews />

      <GiftVouchers />

      <Faq compact />

      {/* Local SEO tags list */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 text-center" data-testid="local-seo">
        <p className="eyebrow mb-6">Salon urody Katowice · Ligota · Ligocka 80</p>
        <p className="text-[var(--beautix-muted)] text-sm max-w-3xl mx-auto leading-relaxed">
          BEAUTIX to nowoczesny <strong className="text-[var(--beautix-ink)]">salon kosmetyczny w Katowicach</strong>{" "}
          specjalizujący się w <Link className="link-underline" to="/stylizacja-paznokci-katowice">stylizacji paznokci</Link>{" "}
          (manicure hybrydowy, żelowy, tytanowy, pedicure), profesjonalnej{" "}
          <Link className="link-underline" to="/podologia-katowice">podologii</Link>{" "}
          (leczenie wrastających paznokci, pedicure leczniczy, usuwanie odcisków) oraz{" "}
          <Link className="link-underline" to="/kosmetyka-twarzy-katowice">kosmetyce twarzy</Link>{" "}
          (oczyszczanie, mezoterapia mikroigłowa, masaż Kobido, stymulatory tkankowe).
          Znajdziesz nas przy <MapPin className="inline w-4 h-4 mb-1" strokeWidth={1.5} /> ul. Ligockiej 80 w Katowicach.
        </p>
      </section>

      <CtaBanner
        title="Umów wizytę w BEAUTIX Katowice."
        subtitle="Zadzwoń, żeby dobrać zabieg do Twoich potrzeb. Elastyczne terminy w tygodniu i w soboty."
      />
    </>
  );
}
